import React, { useCallback, useMemo, useState } from 'react'
import { observer } from 'mobx-react'
import { Input } from 'antd'
import { StarOutlined, StarFilled } from '@ant-design/icons'
import { BottomSheet } from '@pushly/aqe/lib/components/bottom-sheet/bottom-sheet'
import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'
import { useHistory } from 'react-router-dom'
import { Container } from 'typescript-ioc/es5'
import { useAppState } from '../hooks/use-mobile-service'
import { AppService } from '../../services/app'
import { useOpenAccessPath } from '../../core/access/use-open-access-path'
import { simpleNotification } from '../../_utils/utils'
import { PlatformUserDomainRecord } from '../../models/domain/platform-user-domain-record'
import { useSearch } from '../../components/sidebar/active-domain-selector/hooks/use-search'
import { useRecents } from '../../components/sidebar/active-domain-selector/hooks/use-recents'
import { usePinned } from '../../components/sidebar/active-domain-selector/hooks/use-pinned'
import DomainIcon from '../../components/sidebar/active-domain-selector/components/domain-icon'
import './domain-selector-sheet.scss'

const notifyError = (message: string) => simpleNotification('error', message)

interface DomainSelectorSheetProps {
    open: boolean
    onClose: () => void
}

const KEPT_ON_SWITCH = /^\/domains\/\d+\/(dashboard|insights)(\/|$)/

/**
 * The path a switch opens. The dashboard and insights have no ids from the old domain in them, so
 * they stay; any other page (a notification, a compose or edit screen) would load stale ids, so
 * the new domain opens on its dashboard.
 */
function pathForSwitch(currentPath: string, domainId: number): string {
    return KEPT_ON_SWITCH.test(currentPath)
        ? currentPath.replace(/^\/domains\/\d+/, `/domains/${domainId}`)
        : `/domains/${domainId}/dashboard`
}

export const DomainSelectorSheet: React.FC<DomainSelectorSheetProps> = observer(({ open, onClose }) => {
    const appState = useAppState()
    const history = useHistory()
    // The service decides between a router push and a full load when a new version is out.
    const route = useCallback((path: string) => Container.get(AppService).route(path), [])
    const openAccessPath = useOpenAccessPath(route, notifyError)
    const [searchValue, setSearchValue] = useState('')

    const allDomains = appState.currentUserDomains ?? []
    const currentDomainId = appState.currentDomain?.id

    // Domain map for recents/pinned validation
    const domainMap = useMemo((): Map<number, PlatformUserDomainRecord> => {
        const map = new Map<number, PlatformUserDomainRecord>()
        for (const domain of allDomains) {
            map.set(domain.id, domain)
        }
        return map
    }, [allDomains])

    // Hooks from ActiveDomainSelector
    const {
        query,
        setQuery,
        filteredDomains: getFilteredDomains,
    } = useSearch(allDomains.length > 0 ? allDomains : null)

    const userPreferences = useUserPreferences(appState.currentUser?.id)
    const { recents, addToRecents } = useRecents(userPreferences, domainMap, 5)
    const { favorited, togglePin } = usePinned(userPreferences, domainMap, 10)

    // Get filtered domains using the search hook
    const filteredDomains = useMemo(() => getFilteredDomains('all', favorited), [getFilteredDomains, favorited])

    // Build pinned domains list
    const pinnedDomains = useMemo(() => {
        return favorited.map((id) => domainMap.get(id)).filter((d): d is PlatformUserDomainRecord => d != null)
    }, [favorited, domainMap])

    // Build recent domains list (exclude pinned)
    const recentDomains = useMemo(() => {
        const pinnedSet = new Set(favorited)
        return recents
            .filter((id) => !pinnedSet.has(id))
            .map((id) => domainMap.get(id))
            .filter((d): d is PlatformUserDomainRecord => d != null)
    }, [recents, favorited, domainMap])

    // Group filtered domains by org
    const groupedDomains = useMemo(() => {
        const groups = new Map<string, PlatformUserDomainRecord[]>()
        for (const ranked of filteredDomains) {
            const key = ranked.domain.accountName || 'Other'
            if (!groups.has(key)) {
                groups.set(key, [])
            }
            groups.get(key)!.push(ranked.domain)
        }
        const result: { label: string; domains: PlatformUserDomainRecord[] }[] = []
        for (const [label, domains] of groups.entries()) {
            result.push({ label, domains })
        }
        result.sort((a, b) => a.label.localeCompare(b.label))
        return result
    }, [filteredDomains])

    const handleSearchChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value
            setSearchValue(value)
            setQuery(value)
        },
        [setQuery],
    )

    const handleDomainSelect = useCallback(
        async (domain: PlatformUserDomainRecord) => {
            addToRecents(domain.id)
            const opened = await openAccessPath(pathForSwitch(history.location.pathname, domain.id))
            if (!opened) {
                // The error is shown; the sheet stays open so the user can pick another domain.
                return
            }
            setSearchValue('')
            setQuery('')
            onClose()
        },
        [history, openAccessPath, onClose, addToRecents, setQuery],
    )

    const handleClose = useCallback(() => {
        setSearchValue('')
        setQuery('')
        onClose()
    }, [onClose, setQuery])

    const handleTogglePin = useCallback(
        (e: React.MouseEvent, domainId: number) => {
            e.stopPropagation()
            togglePin(domainId)
        },
        [togglePin],
    )

    const isSearching = searchValue.trim().length > 0

    const renderDomainItem = (domain: PlatformUserDomainRecord) => {
        const isActive = domain.id === currentDomainId
        const isPinned = favorited.includes(domain.id)

        return (
            <button
                key={domain.id}
                className={`domain-selector-sheet__domain-item${
                    isActive ? ' domain-selector-sheet__domain-item--active' : ''
                }`}
                role="option"
                aria-selected={isActive}
                onClick={() => handleDomainSelect(domain)}
                type="button"
            >
                <div className="domain-selector-sheet__domain-icon">
                    <DomainIcon domain={domain} />
                </div>
                <div className="domain-selector-sheet__domain-info">
                    <div className="domain-selector-sheet__domain-display-name">{domain.displayName}</div>
                    <div className="domain-selector-sheet__domain-name">{domain.name}</div>
                </div>
                <div
                    className={`domain-selector-sheet__pin-toggle${
                        isPinned ? ' domain-selector-sheet__pin-toggle--active' : ''
                    }`}
                    onClick={(e) => handleTogglePin(e, domain.id)}
                    role="button"
                    aria-label={isPinned ? `Unpin ${domain.displayName}` : `Pin ${domain.displayName}`}
                    tabIndex={-1}
                >
                    {isPinned ? <StarFilled /> : <StarOutlined />}
                </div>
            </button>
        )
    }

    return (
        <BottomSheet open={open} onClose={handleClose} height={70} closable={false} autoFocus={false}>
            <div className="domain-selector-sheet">
                <div className="domain-selector-sheet__search">
                    <Input.Search
                        placeholder="Search domains..."
                        value={searchValue}
                        onChange={handleSearchChange}
                        allowClear
                        aria-label="Search domains"
                    />
                </div>

                <div className="domain-selector-sheet__content" role="listbox" aria-label="Domain list">
                    {filteredDomains.length === 0 && (
                        <div className="domain-selector-sheet__empty">No domains found</div>
                    )}

                    {filteredDomains.length > 0 && (
                        <>
                            {/* Pinned section */}
                            {!isSearching && pinnedDomains.length > 0 && (
                                <div className="domain-selector-sheet__group">
                                    <div className="domain-selector-sheet__group-label" aria-hidden="true">
                                        Pinned
                                    </div>
                                    {pinnedDomains.map(renderDomainItem)}
                                </div>
                            )}

                            {/* Recents section */}
                            {!isSearching && recentDomains.length > 0 && (
                                <div className="domain-selector-sheet__group">
                                    <div className="domain-selector-sheet__group-label" aria-hidden="true">
                                        Recent
                                    </div>
                                    {recentDomains.map(renderDomainItem)}
                                </div>
                            )}

                            {/* All domains grouped by org */}
                            {groupedDomains.map((group) => (
                                <div key={group.label} className="domain-selector-sheet__group">
                                    <div className="domain-selector-sheet__group-label" aria-hidden="true">
                                        {group.label}
                                    </div>
                                    {group.domains.map(renderDomainItem)}
                                </div>
                            ))}
                        </>
                    )}
                </div>
            </div>
        </BottomSheet>
    )
})

DomainSelectorSheet.displayName = 'DomainSelectorSheet'
